/**
 * Parts API Service — Wadakaroo
 *
 * Mirrors every endpoint on PartController:
 *
 *   GET    /api/repair-parts                         List parts (paginated)
 *   GET    /api/repair-parts/{uuid}                  Get single part
 *   POST   /api/repair-parts                         Create part  (stock:write)
 *   PUT    /api/repair-parts/{uuid}                  Update part  (stock:write)
 *   PATCH  /api/repair-parts/{uuid}/activate         Activate     (stock:write)
 *   PATCH  /api/repair-parts/{uuid}/deactivate       Deactivate   (stock:write)
 *   DELETE /api/repair-parts/{uuid}                  Delete       (stock:adjust)
 */

import axiosInstance from './axiosInstance';

/*
 * ⚠️ BASE is /repair-parts, not /parts.
 *
 * This file used to call /parts, which was WRONG and had been since changelog 83
 * renamed the workshop's own parts catalogue from `part` to `repair_part`. The UI was
 * never repointed, so /parts reached the parts-MARKETPLACE listing controller instead —
 * a different entity with different fields (title/price/condition, not
 * name/manufacturerPartId/cost) and different permissions (parts:read, not stock:read).
 * activate/deactivate below exist only on RepairPartController, so those calls had been
 * 404ing outright.
 *
 * Found on 2026-08-22 while deleting the marketplace: removing the marketplace would
 * have broken Stock and the Service-Profile parts picker, and the deletion would have
 * been blamed for a fault that predated it by months.
 */

const BASE = '/repair-parts';

// ─── List ─────────────────────────────────────────────────────────────────────

/**
 * GET /api/repair-parts
 *
 * @param {Object}  params
 * @param {number}  [params.page=0]
 * @param {number}  [params.size=20]
 * @param {boolean} [params.activeOnly=false]
 * @param {string}  [params.make]              Optional filter
 * @param {string}  [params.model]             Optional filter
 * @param {string}  [params.name]              Optional filter
 * @param {string}  [params.manufacturerPartId] Optional filter
 * @returns {Promise<Page<PartResponse>>}
 */
export const listPartsApi = async ({
                                       page = 0,
                                       size = 20,
                                       activeOnly = false,
                                       make,
                                       model,
                                       name,
                                       manufacturerPartId,
                                   } = {}) => {
    const params = new URLSearchParams({
        page: String(page),
        size: String(size),
        activeOnly: String(activeOnly),
    });
    if (make?.trim())                 params.set('make',                make.trim());
    if (model?.trim())                params.set('model',               model.trim());
    if (name?.trim())                 params.set('name',                name.trim());
    if (manufacturerPartId?.trim())   params.set('manufacturerPartId',  manufacturerPartId.trim());

    const res = await axiosInstance.get(`${BASE}?${params}`);
    return res.data;
};

// ─── Get single ───────────────────────────────────────────────────────────────

/**
 * GET /api/repair-parts/{uuid}
 * @param {string} uuid
 * @returns {Promise<PartResponse>}
 */
export const getPartApi = async (uuid) => {
    const res = await axiosInstance.get(`${BASE}/${uuid}`);
    return res.data;
};

// ─── Create ───────────────────────────────────────────────────────────────────

/**
 * POST /api/repair-parts
 * @param {{
 *   name: string,
 *   manufacturerPartId?: string,
 *   make?: string,
 *   model?: string,
 *   warrantyYears?: number,
 *   cost?: number
 * }} data
 * @returns {Promise<PartResponse>}
 */
export const createPartApi = async (data) => {
    const res = await axiosInstance.post(BASE, data);
    return res.data;
};

// ─── Update ───────────────────────────────────────────────────────────────────

/**
 * PUT /api/repair-parts/{uuid}
 * @param {string} uuid
 * @param {{
 *   name: string,
 *   manufacturerPartId?: string,
 *   make?: string,
 *   model?: string,
 *   warrantyYears?: number,
 *   cost?: number
 * }} data
 * @returns {Promise<PartResponse>}
 */
export const updatePartApi = async (uuid, data) => {
    const res = await axiosInstance.put(`${BASE}/${uuid}`, data);
    return res.data;
};

// ─── Activate / Deactivate ────────────────────────────────────────────────────

/**
 * PATCH /api/repair-parts/{uuid}/activate
 * @param {string} uuid
 * @returns {Promise<PartResponse>}
 */
export const activatePartApi = async (uuid) => {
    const res = await axiosInstance.patch(`${BASE}/${uuid}/activate`);
    return res.data;
};

/**
 * PATCH /api/repair-parts/{uuid}/deactivate
 * @param {string} uuid
 * @returns {Promise<PartResponse>}
 */
export const deactivatePartApi = async (uuid) => {
    const res = await axiosInstance.patch(`${BASE}/${uuid}/deactivate`);
    return res.data;
};

// ─── Delete ───────────────────────────────────────────────────────────────────

/**
 * DELETE /api/repair-parts/{uuid}
 * Requires stock:adjust permission.
 * @param {string} uuid
 * @returns {Promise<void>}
 */
export const deletePartApi = async (uuid) => {
    await axiosInstance.delete(`${BASE}/${uuid}`);
};

// ─── Default export ────────────────────────────────────────────────────────────

const partsApi = {
    listPartsApi,
    getPartApi,
    createPartApi,
    updatePartApi,
    activatePartApi,
    deactivatePartApi,
    deletePartApi,
};

export default partsApi;